import { Helmet } from "react-helmet-async";
import { CURSUS_SEO, CURSUS_HERO_IMAGE, CURSUS_FAQ } from "@/lib/cursusMeta";

const SITE_URL = "https://cursus-physio.fr";

export default function CursusSeo({ cursus, formations = [] }) {
    const seo = CURSUS_SEO[cursus.slug] || {
        title: `${cursus.name} | Cursus Physio`,
        description: cursus.tagline || "",
    };
    const url = `${SITE_URL}/cursus/${cursus.slug}`;
    const image = CURSUS_HERO_IMAGE[cursus.slug] || cursus.image_url || "";

    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Cursus", item: `${SITE_URL}/cursus` },
            { "@type": "ListItem", position: 3, name: cursus.name, item: url },
        ],
    };

    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        name: cursus.name,
        description: cursus.description || cursus.tagline,
        url,
        image,
        provider: {
            "@type": "Organization",
            name: "Cursus Physio",
            sameAs: SITE_URL,
        },
        hasCourseInstance: formations.map((f) => ({
            "@type": "CourseInstance",
            name: f.name,
            description: f.short_description,
            courseMode: "Onsite",
            timeRequired: `PT${f.duration_hours || 7}H`,
            url: `${SITE_URL}/formations/${f.slug}`,
        })),
        offers: {
            "@type": "Offer",
            price: "1170",
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: `${SITE_URL}/sessions`,
        },
    };

    // Build FAQ schema from static meta
    const faqItems = (CURSUS_FAQ[cursus.slug] || []).map((qa) => ({
        "@type": "Question",
        name: qa.q,
        acceptedAnswer: { "@type": "Answer", text: qa.a },
    }));
    const faqSchema = faqItems.length
        ? {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqItems,
          }
        : null;

    return (
        <Helmet>
            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            <link rel="canonical" href={url} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content={image} />
            <meta property="og:url" content={url} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={image} />
            <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
            <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
            {faqSchema && (
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            )}
        </Helmet>
    );
}
